/**
 * @copyright Copyright 2019 Epic Systems Corporation
 * @file buildAsyncExecutor internal helper function
 * @author Roland Scott
 * @module react-async-hook\internal\use-async\build-async-executor
 */

import { IAsyncFunction } from "../";
import { Executor } from "../../";
import { buildAsyncFinisher } from "./build-async-finisher";
import { AsyncFinishersRef, CallbackExecutorRef, NextIdRef, StateUpdaterRef } from "../types";

/**
 * Build the function that will actually kick off the async execution and handle
 * the promise return.
 * @param asyncOperation The async operation
 * @param asyncFinishers React ref to the current list of async finisher functions
 * @param executeCallbacks React ref to the current function used to execute callbacks
 * @param nextId React ref to the next execution ID to use
 * @param updateState React ref to the current function used to update state
 */
export function buildAsyncExecutor<TAsync extends IAsyncFunction>(
	asyncOperation: TAsync,
	asyncFinishers: AsyncFinishersRef,
	executeCallbacks: CallbackExecutorRef,
	nextId: NextIdRef,
	updateState: StateUpdaterRef,
): Executor<TAsync> {
	function executor(...args: any[]): number {
		const id = (nextId.current = nextId.current + 1);

		// Attempt to update the state. Will be a no-op if the tracker component has already unmounted.
		updateState.current({ type: "start", id });

		// Create a finisher function for this async.
		asyncFinishers.current[id] = buildAsyncFinisher(id, updateState, executeCallbacks);

		// Execute the async operation.
		asyncOperation(...args)
			.then((result) => {
				asyncFinishers.current[id](result, undefined, { params: args });
			})
			.catch((error) => {
				asyncFinishers.current[id](undefined, error, { params: args });
			});

		// Return the ID so that caller can ignore this execution later, if need be.
		return id;
	}
	return executor as any;
}
